import React, { useEffect, useState } from "react";
import { Grid, Box } from "@mui/material";
import { motion } from "framer-motion";

import GENAITextBlock from "./GENAITextBlock";
import GENAICardBlock from "./GENAICardBlock";
import GENAITileBlock from "./GENAITileBlock";
import GENAIChartBlock from "./GENAIChartBlock";
import GENAICalendar from "./GENAICalendar";
import GENAITree from "./GENAITree";
import GENAILinkBlock from "./GENAILinkBlock";
import GENAITableBlock from "./GENAITableBlock";
import GENAIImageCard from "./GENAIImageCard";
import GENAIVideoCard from "./GENAIVideoCard";
import SuggestedQuestions from "./SuggestedQuestions";

import {
  Block,
  BlockType,
  TextBlock,
  TableBlock,
  CardBlock,
  TileBlock,
  LinkBlock,
  ChartBlock,
  ImageBlock,
  VideoBlock,
  CalendarBlock,
  TreeBlock,
  SuggestedQuestionsBlock,
} from "../Utils/ComponentsUtils";

const BLOCK_RENDERERS: Record<BlockType, (block: any, setQuestion?: (q: string) => void, handleSubmit?: () => void) => JSX.Element> = {
  text: (block: TextBlock) => <GENAITextBlock content={block.content} />,
  table: (block: TableBlock) => <GENAITableBlock headers={block.headers} rows={block.rows} />,
  card: (block: CardBlock) => <GENAICardBlock {...block} />,
  tile: (block: TileBlock) => <GENAITileBlock {...block} />,
  link: (block: LinkBlock) => <GENAILinkBlock text={block.text} url={block.url} />,
  chart: (block: ChartBlock) => <GENAIChartBlock chartType={block.chartType} title={block.title} data={block.data} />,
  image: (block: ImageBlock) => <GENAIImageCard {...block} />,
  video: (block: VideoBlock) => <GENAIVideoCard {...block} />,
  calendar: (block: CalendarBlock) => <GENAICalendar {...block} />,
  tree: (block: TreeBlock) => <GENAITree {...block} />,
  suggested_questions: (block: SuggestedQuestionsBlock, setQuestion, handleSubmit) => (
    <SuggestedQuestions
      questions={block.questions || []}
      onSelect={(selected) => {
        if (setQuestion && handleSubmit) {
          setQuestion(selected);
          handleSubmit();
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      }}
    />
  ),
};

const GENAIRenderer: React.FC<{
  blocks: Block[];
  setQuestion?: (q: string) => void;
  handleSubmit?: () => void;
  renderAll?: boolean;
  disableMotion?: boolean;
}> = ({ blocks = [], setQuestion, handleSubmit, renderAll = false, disableMotion = false }) => {
  const [visibleBlocks, setVisibleBlocks] = useState<Block[]>([]);

  useEffect(() => {
    const safeBlocks = Array.isArray(blocks) ? blocks : [];
    setVisibleBlocks(renderAll ? safeBlocks : []);
    if (renderAll) return;

    let idx = 0;
    const interval = setInterval(() => {
      if (idx >= safeBlocks.length) {
        clearInterval(interval);
        return;
      }
      const nextBlock = safeBlocks[idx];
      if (nextBlock) {
        setVisibleBlocks((prev) => [...prev, nextBlock]);
      }
      idx++;
    }, 1000);

    return () => clearInterval(interval);
  }, [blocks, renderAll]);

  const safeVisibleBlocks = Array.isArray(visibleBlocks) ? visibleBlocks : [];

  const sortedVisibleBlocks = [...safeVisibleBlocks]
    .filter((block) => block.type !== "suggested_questions")
    .sort((a, b) => {
      const rowA = a?.row ?? 0;
      const rowB = b?.row ?? 0;
      if (rowA !== rowB) return rowA - rowB;
      const colA = a?.column ?? 0;
      const colB = b?.column ?? 0;
      return colA - colB;
    });

  const suggestedBlock = safeVisibleBlocks.find((block) => block.type === "suggested_questions") as SuggestedQuestionsBlock | undefined;

  const grouped: Record<number, Block[]> = {};
  sortedVisibleBlocks.forEach((block) => {
    const row = block.row ?? 0;
    if (!grouped[row]) grouped[row] = [];
    grouped[row].push(block);
  });

  const sortedRows = Object.entries(grouped).sort(([a], [b]) => Number(a) - Number(b));

  return (
    <Box sx={{}}>
      {sortedRows.map(([rowKey, rowBlocks]) => (
        <Grid
          container
          spacing={2}
          key={`row-${rowKey}`}
          sx={{ mb: 2, alignItems: "stretch" }}
        >
          {rowBlocks.map((block, idx) => {
            const Renderer = BLOCK_RENDERERS[block.type];
            if (!Renderer) return null;

            const totalCols = block.total_columns || 1;
            const gridSize = totalCols >= 1 && totalCols <= 12 ? Math.floor(12 / totalCols) : 12;

            return (
              <Grid
                item
                xs={12}
                sm={12}
                md={gridSize}
                lg={gridSize}
                key={idx}
                sx={{ display: "flex", flexDirection: "column" }}
              >
                {disableMotion ? (
                  <Box sx={{ flexGrow: 1, display: "flex" }}>
                    {Renderer(block, setQuestion, handleSubmit)}
                  </Box>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      delay: idx * 0.04,
                      duration: 0.35,
                      ease: "easeOut",
                    }}
                    style={{ flexGrow: 1, display: "flex" }}
                  >
                    {Renderer(block, setQuestion, handleSubmit)}
                  </motion.div>
                )}
              </Grid>
            );
          })}
        </Grid>
      ))}
    </Box>
  );
};

export default GENAIRenderer;
